import { Controller } from '@hotwired/stimulus';

/**
 * SmartSearchComponentController - Global ID search with inline results.
 *
 * Global ID format: ca_usr_01arz3ndektsv4rrffq69g5fav
 * - Region prefix (ca/eu)
 * - Model prefix (usr/bac/elc)
 * - 26-char ULID
 */

interface SearchAction {
  type: string;
  label: string;
  url: string;
  primary?: boolean;
}

interface SearchResult {
  found: boolean;
  type: string;
  type_display: string;
  global_id: string;
  name: string;
  email: string;
  actions: SearchAction[];
}

export default class extends Controller<HTMLElement> {
  static targets = [
    'input',
    'clearButton',
    'loading',
    'notFound',
    'notFoundMessage',
    'results',
    'badge',
    'region',
    'globalId',
    'name',
    'email',
    'actionsContainer',
  ];
  static values = {
    url: String,
  };

  declare readonly inputTarget: HTMLInputElement;
  declare readonly clearButtonTarget: HTMLButtonElement;
  declare readonly loadingTarget: HTMLElement;
  declare readonly notFoundTarget: HTMLElement;
  declare readonly notFoundMessageTarget: HTMLElement;
  declare readonly resultsTarget: HTMLElement;
  declare readonly badgeTarget: HTMLElement;
  declare readonly regionTarget: HTMLElement;
  declare readonly globalIdTarget: HTMLElement;
  declare readonly nameTarget: HTMLElement;
  declare readonly emailTarget: HTMLElement;
  declare readonly actionsContainerTarget: HTMLElement;
  declare urlValue: string;

  private abortController: AbortController | null = null;

  private readonly regionNames: Record<string, string> = {
    na: '🇨🇦 North America',
    ca: '🇨🇦 North America',
    eu: '🇪🇺 Europe',
  };

  connect(): void {
    this.hideAll();
    this.clearButtonTarget.hidden = true;
    this.checkClipboardOnLoad();
  }

  private async checkClipboardOnLoad(): Promise<void> {
    try {
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        return;
      }

      const clipboardText = await navigator.clipboard.readText();
      const trimmed = clipboardText?.trim();

      if (trimmed && this.isGlobalId(trimmed)) {
        this.inputTarget.value = trimmed;
        this.clearButtonTarget.hidden = false;
        this.processInput(trimmed);
      }
    } catch {
      // Clipboard access denied - user will paste manually
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text')?.trim();
    if (!pastedText) return;

    setTimeout(() => {
      this.processInput(pastedText);
    }, 10);
  }

  onInput(): void {
    const value = this.inputTarget.value.trim();

    if (value.length === 0) {
      this.hideAll();
      this.clearButtonTarget.hidden = true;
    } else {
      this.clearButtonTarget.hidden = false;
      if (this.isGlobalId(value)) {
        this.processInput(value);
      } else {
        this.hideAll();
      }
    }
  }

  clear(): void {
    this.inputTarget.value = '';
    this.hideAll();
    this.clearButtonTarget.hidden = true;
    this.inputTarget.focus();

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private isGlobalId(text: string): boolean {
    const pattern = /^[a-z]{2}_[a-z]{3}_[a-z0-9]{26}$/i;
    return pattern.test(text);
  }

  private parseGlobalId(
    globalId: string
  ): { region: string; model: string; uuid: string } | null {
    if (!this.isGlobalId(globalId)) return null;

    const [region, model, uuid] = globalId.toLowerCase().split('_');
    return { region, model, uuid };
  }

  private async processInput(text: string): Promise<void> {
    const parsed = this.parseGlobalId(text);
    if (!parsed) {
      this.hideAll();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.showLoading();
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${this.urlValue}?global_id=${encodeURIComponent(text)}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          signal: this.abortController.signal,
        }
      );

      const data: SearchResult = await response.json();

      if (data.found) {
        this.showResult(data, parsed);
      } else {
        this.showNotFound(
          (data as { error?: string }).error || 'Record not found'
        );
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      console.error('Smart search error:', error);
      this.showNotFound('An error occurred while searching');
    } finally {
      this.abortController = null;
    }
  }

  private showLoading(): void {
    this.hideAll();
    this.loadingTarget.hidden = false;
  }

  private showNotFound(message: string): void {
    this.hideAll();
    this.notFoundMessageTarget.textContent = message;
    this.notFoundTarget.hidden = false;
  }

  private showResult(
    data: SearchResult,
    parsed: { region: string; model: string; uuid: string }
  ): void {
    this.hideAll();

    const regionName =
      this.regionNames[parsed.region] || parsed.region.toUpperCase();

    this.badgeTarget.textContent = data.type_display;
    this.regionTarget.textContent = regionName;
    this.globalIdTarget.textContent = data.global_id;
    this.nameTarget.textContent = data.name || 'N/A';
    this.emailTarget.textContent = data.email || 'N/A';

    this.renderActions(data);

    this.resultsTarget.hidden = false;
  }

  private renderActions(data: SearchResult): void {
    // Clear existing actions
    this.actionsContainerTarget.innerHTML = '';

    data.actions.forEach((action) => {
      const link = document.createElement('a');
      link.href = action.url;
      link.target = '_blank';
      link.className = 'toolkit-smart-search__results-link';

      if (action.type === 'impersonate') {
        link.classList.add('toolkit-smart-search__results-link--impersonate');
      }

      link.textContent = `${action.label} →`;
      this.actionsContainerTarget.appendChild(link);
    });
  }

  private hideAll(): void {
    this.loadingTarget.hidden = true;
    this.notFoundTarget.hidden = true;
    this.resultsTarget.hidden = true;
  }
}
